import { Avatar, AvatarFallback } from '@/components/base/avatar'
import { ScrollArea } from '@/components/base/scroll-area'
import { ButtonParticipant } from '@/components/ButtonParticipant'
import { ButtonSortAlpha } from '@/components/ButtonSortAlpha'
import { Icon } from '@/components/common/icons/Icon'
import { Feature } from '@/components/Feature'
import {
  AllMemberMicrophoneButton,
  ParticipantMemberButtons,
} from '@/components/ParticipantMemberButtons'
import { ParticipantSelfButtons } from '@/components/ParticipantSelfButtons'
import { SearchButton } from '@/components/SearchButton'
import { SORT_ORDER, TEST_IDS } from '@/helpers/constants'
import { cn, sortBy } from '@/helpers/utils'
import { useSimpleSearch } from '@/hooks/useSimpleSearch'
import { useSortOrder } from '@/hooks/useSortOrder'
import { logger } from '@/logger/createLogger'
import { FEATURES } from '@/stores/features'
import { useRoomStore, useRoomStoreActions } from '@/stores/room'
import { useUiStoreActions } from '@/stores/ui'
import { useUserStore } from '@/stores/user'
import { useMemo } from 'react'

export const PanelRoomParticipants = () => {
  const membersMap = useRoomStore(state => state.membersMap)
  const selfMemberId = useUserStore(state => state.memberId)

  const { sortOrder, toggleSortOrder } = useSortOrder()

  const { getMembersState } = useRoomStoreActions()

  const sortedMembers = useMemo(() => {
    const membersState = getMembersState(membersMap)
    return sortBy(membersState, sortOrder, 'name')
  }, [getMembersState, membersMap, sortOrder])

  const { searchResults, handleSearch, handleSearchReset, searchTerm } =
    useSimpleSearch({
      items: sortedMembers,
      key: 'name',
    })

  return (
    <section data-testid={TEST_IDS.ROOM_PARTICIPANTS_PANEL}>
      <div className="absolute right-0 top-0 mr-10 translate-y-[2px]">
        <ul
          aria-label="Participant actions"
          className="flex items-center gap-x-2"
        >
          <li>
            <ButtonSortAlpha
              isAsc={sortOrder === SORT_ORDER.ASC}
              onClick={toggleSortOrder}
            />
          </li>
          <li>
            <SearchButton
              buttonLabel="Search Participants"
              // TODO: the width allows the search to grow to cover the panel title
              className="w-[286px]"
              formDescription="Search for a participant"
              handleSearch={handleSearch}
              handleSearchReset={handleSearchReset}
              placeholder="Search Participants"
            />
          </li>
          <Feature name={FEATURES.PARTICIPANTS_MUTE_ALL}>
            <li>
              <AllMemberMicrophoneButton />
            </li>
          </Feature>
        </ul>
      </div>
      {searchTerm && !searchResults.length && (
        <div className="flex basis-full flex-col items-center justify-center">
          <div className="flex items-center gap-x-2">
            <Icon tag="warning" />
            <h3 className="text-xl font-semibold">Participant not found</h3>
          </div>
          <p className="flex flex-col items-center p-5 font-montserrat text-sm">
            <span>No results found for</span>
            <span className="py-2 font-semibold italic"> {searchTerm}</span>
            <span> in the participants list.</span>
          </p>
        </div>
      )}
      {searchResults.length > 0 && (
        <ScrollArea className="m-0 py-4 text-sm text-foreground">
          <ul aria-label="Participants list">
            {searchResults.map(member => (
              <Participant
                addressId={member.subscriberId}
                audioMuted={member.audioMuted}
                key={member.id}
                participantId={member.id}
                participantName={member.name}
                isSelf={member.subscriberId === selfMemberId}
                videoMuted={member.videoMuted}
                memberType={member.type}
              />
            ))}
          </ul>
        </ScrollArea>
      )}
    </section>
  )
}

interface ParticipantProps {
  addressId: string
  audioMuted: boolean
  isSelf: boolean
  memberType: string,
  participantId: string,
  participantName: string,
  videoMuted: boolean
}

const Participant = ({
  addressId,
  audioMuted,
  participantId,
  isSelf,
  participantName,
  videoMuted,
  memberType,
}: ParticipantProps) => {
  const { openRoomPanel, setAdvancedPanel } = useUiStoreActions()

  const userMetaData = useUserStore(state =>
    state.userMetaDataMap.get(addressId),
  )

  const handleOpenAdvancedControls = () => {
    // open advanced controls panel for this participant
    openRoomPanel()
    setAdvancedPanel(participantId)
  }

  const userColor = userMetaData?.color
  const userInitial = userMetaData?.initial

  if (!userColor || !userInitial) {
    logger.error('missing userMetaData in participant', {
      addressId,
      participantId,
      participantName,
      userMetaData,
    })
  }

  const isScreenSharing = memberType === 'screen';

  return (
    <li className="flex w-full items-center justify-between gap-x-2 p-2">
      <Avatar className="h-10 w-10">
        {/* FIXME: add avatar image */}
        <AvatarFallback
            className={cn(
                'border border-transparent uppercase text-background flex items-center justify-center',
                userColor,
                isSelf && !isScreenSharing && 'border-deep-sea text-deep-sea',
                isScreenSharing && 'bg-muted text-primary-foreground',
            )}
        >
          {!isScreenSharing && userInitial}
          {isScreenSharing && <Icon tag="laptop" size="xl" />}
        </AvatarFallback>
      </Avatar>
      {/* FIXME: Should the name use a tooltip if the name is truncated? */}
      <p className="w-full truncate">{participantName}</p>

      <div className="flex items-center justify-center gap-x-2">
        {isSelf ? (
          <Feature name={FEATURES.PARTICIPANTS_SELF_BUTTONS}>
            <ParticipantSelfButtons
              audioMuted={audioMuted}
              participantId={participantId}
              participantName={participantName}
              videoMuted={videoMuted}
            />
          </Feature>
        ) : (
          <Feature name={FEATURES.PARTICIPANTS_MEMBER_BUTTONS}>
            <ParticipantMemberButtons
              audioMuted={audioMuted}
              participantId={participantId}
              participantName={participantName}
              videoMuted={videoMuted}
            />
          </Feature>
        )}

        <ButtonParticipant onClick={handleOpenAdvancedControls} tag="settings">
          <span className="sr-only">
            Open Advanced Controls for {participantName}
          </span>
        </ButtonParticipant>
      </div>
    </li>
  )
}
